import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { COMPONENT } from '@adalo/constants'
import { getScreens } from 'ducks/apps'
import { getDatasources } from 'ducks/apps/datasources'
import { getObjectList, getMap, State } from 'ducks/editor/objects/selectors'
import getParentScreen from 'ducks/editor/objects/helpers/getParentScreen'
import { SearchResult } from './types'

/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */

export const useSearchableItems = (
  appId: string,
  enabled = true
): SearchResult[] => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const screens = useSelector((state: State) => getScreens(state, appId))
  const datasources = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (state: State) => getDatasources(state, appId)
  )
  const objectList = useSelector(getObjectList)
  const pathMap = useSelector(getMap)

  return useMemo(() => {
    if (!enabled) return []

    const results: SearchResult[] = []

    // Screens
    if (screens) {
      for (const screen of screens) {
        results.push({
          id: `screen-${String(screen.id)}`,
          name: String(screen.name || 'Untitled Screen'),
          category: 'screen',
          screenObject: screen,
        })
      }
    }

    // Collections (tables from datasources)
    if (datasources) {
      for (const ds of datasources) {
        const tables = ds.tables || {}

        for (const tableId of Object.keys(tables)) {
          const table = tables[tableId]

          results.push({
            id: `collection-${String(ds.id)}-${tableId}`,
            name: String(table.name || 'Untitled Collection'),
            category: 'collection',
            datasourceId: String(ds.id),
            tableId,
          })
        }
      }
    }

    // Components (non-screen objects in the editor tree)
    if (objectList && pathMap) {
      const traverse = (objects: unknown[]) => {
        for (const obj of objects) {
          const o = obj as Record<string, unknown>
          const objType = o.type as string
          const objId = o.id as string
          const objName = o.name as string | undefined

          if (objType === COMPONENT) continue

          if (objName && objId) {
            const parentScreen = getParentScreen(objectList, pathMap, objId)

            results.push({
              id: `component-${objId}`,
              name: objName,
              category: 'component',
              componentId: objId,
              parentName: parentScreen
                ? String(parentScreen.name) || undefined
                : undefined,
              parentScreenObject: parentScreen || undefined,
            })
          }

          const children = o.children || o.objects

          if (Array.isArray(children)) {
            traverse(children)
          }
        }
      }

      for (const screen of objectList) {
        const s = screen as Record<string, unknown>
        const children = s.children || s.objects

        if (Array.isArray(children)) {
          traverse(children)
        }
      }
    }

    return results
  }, [enabled, screens, datasources, objectList, pathMap])
}

/* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
